import React from "react";
import { Box, Container, Typography, Grid, Avatar, Link } from "@mui/material";

const teamMembers = [
  {
    name: "Gustav Nilsonne",
    title: "Coordinator, Associate Professor",
    email: "gustav.nilsonne@ki.se",
    description:
      "Gustav is an associate professor in neuroscience and coordinator at Karolinska Institutet. His research focuses on metascience, transparency, and reproducibility in medical research, as well as neuroscience and translational research.",
    avatar: "/byline/gustavnilsonne.jpg",
    links: [
      { title: "Email", url: "mailto:gustav.nilsonne@ki.se" },
      { title: "ORCID", url: "https://orcid.org/0000-0001-5273-0150" },
      { title: "nilsonne.net", url: "https://nilsonne.net" },
      { title: "KI Profile", url: "https://ki.se/personer/gustav-nilsonne" },
    ],
  },
  {
    name: "Till Bruckner",
    title: "Postdoctoral Researcher",
    email: "till.bruckner@ki.se",
    description:
      "Till is a postdoctoral researcher focusing on interventions to improve clinical trial reporting. His research interests include clinical trials, metascience, research integrity, open science, and research policy. He is also the founder of TranspariMED, a project that aims to improve clinical trial reporting.",
    avatar: "/byline/tillbruckner.jpeg",
    links: [
      { title: "Email", url: "mailto:till.bruckner@ki.se" },
      { title: "TranspariMED", url: "https://transparimed.org" },
      { title: "ORCID", url: "https://orcid.org/0000-0001-9346-3319" },
      { title: "KI Profile", url: "https://ki.se/en/people/till-bruckner" },
    ],
  },
  {
    name: "Laura Caquelin",
    title: "Postdoctoral Researcher",
    email: "laura.caquelin@ki.se",
    description:
      "Laura is a postdoctoral researcher in metascience. Her current work focuses on testing interventions to improve research reproducibility within the European-funded iRISE project. She has a background in biomedical sciences and clinical research.",
    avatar: "/byline/lauracaquelin.jpeg",
    links: [
      { title: "Email", url: "mailto:laura.caquelin@ki.se" },
      { title: "ORCID", url: "https://orcid.org/0000-0003-4557-3315" },
      { title: "KI Profile", url: "https://ki.se/en/people/laura-caquelin" },
    ],
  },
  {
    name: "Love Ahnström",
    title: "Research Assistant, Medical Student",
    email: "love.ahnstrom@ki.se",
    description:
      "Love is a research assistant and medical student at Karolinska Institutet. He specializes in software development and data analysis.",
    avatar: "/byline/loveahnstrom.jpg",
    links: [
      { title: "Email", url: "mailto:love.ahnstrom@ki.se" },
      { title: "GitHub", url: "https://github.com/lahnstrom" },
      { title: "ORCID", url: "https://orcid.org/0009-0007-8332-958X" },
      { title: "KI Profile", url: "https://ki.se/personer/love-ahnstrom" },
    ],
  },
];

const getInitials = (name) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("");

const ResearchTeam = () => {
  return (
    <Container maxWidth="md" sx={{ py: 8, marginTop: 4 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Research Team
      </Typography>
      <Typography
        variant="subtitle1"
        align="center"
        color="text.secondary"
        paragraph
      >
        Meet the members of our research team at Karolinska Institutet.
      </Typography>
      <Grid container spacing={4}>
        {teamMembers.map((member) => (
          <Grid item xs={12} md={6} key={member.email}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 2,
                px: 2,
                minHeight: 240,
                maxWidth: 400,
                textAlign: "center",
              }}
            >
              {member.avatar ? (
                <Avatar
                  src={member.avatar}
                  alt={`${member.name} photo`}
                  sx={{
                    width: 120,
                    height: 120,
                    mb: 2,
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    mb: 2,
                    bgcolor: "#870052",
                    fontSize: 48,
                  }}
                >
                  {getInitials(member.name)}
                </Avatar>
              )}
              <Typography variant="h6" gutterBottom>
                {member.name}
              </Typography>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
                sx={{ maxWidth: 400 }}
              >
                {member.title}
              </Typography>

              {/* Links Section - replacing email */}
              {member.links && member.links.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  {member.links.map((link, index) => (
                    <React.Fragment key={index}>
                      <Link
                        href={link.url}
                        target={link.title === "Email" ? "_self" : "_blank"}
                        rel={
                          link.title === "Email" ? "" : "noopener noreferrer"
                        }
                        color="#FF876F"
                        underline="hover"
                        sx={{ fontSize: 14 }}
                      >
                        {link.title}
                      </Link>
                      {index < member.links.length - 1 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: 14 }}
                        >
                          |
                        </Typography>
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              )}

              <Typography variant="body2" color="text.secondary">
                {member.description}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default ResearchTeam;
